import { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Trophy, Sparkle, UserCircle, Plus } from "@phosphor-icons/react";

/**
 * GameLobby — Select mode (singles/doubles), then characters for player + (partner) + opponents.
 * After selection, calls /api/game/match/start and passes matchState up to the parent.
 */
export default function GameLobby({ characters = [], dydCost = 0, onStartMatch }) {
  const [mode, setMode] = useState("singles"); // "singles" | "doubles"
  const [step, setStep] = useState("mode"); // mode -> player -> partner (if doubles) -> opponents -> ready
  const [playerChar, setPlayerChar] = useState(null);
  const [partnerChar, setPartnerChar] = useState(null);
  const [opponents, setOpponents] = useState([]);
  const [starting, setStarting] = useState(false);

  const tierColor = {
    elite: "bg-[#CCFF00]/20 text-[#CCFF00] border-[#CCFF00]/40",
    legendary: "bg-amber-500/20 text-amber-300 border-amber-500/40",
    standard: "bg-blue-500/20 text-blue-300 border-blue-500/40",
  };

  const next = () => {
    if (step === "mode") setStep("player");
    else if (step === "player" && playerChar) {
      if (mode === "doubles") setStep("partner");
      else setStep("opponents");
    } else if (step === "partner" && partnerChar) setStep("opponents");
    else if (step === "opponents") {
      const needed = mode === "doubles" ? 2 : 1;
      if (opponents.length === needed) setStep("ready");
      else toast.error(`Selecciona ${needed} oponente${needed > 1 ? "s" : ""}`);
    }
  };

  const back = () => {
    if (step === "player") setStep("mode");
    else if (step === "partner") setStep("player");
    else if (step === "opponents") setStep(mode === "doubles" ? "partner" : "player");
    else if (step === "ready") setStep("opponents");
  };

  const toggleOpponent = (id) => {
    if (id === playerChar?.id || id === partnerChar?.id) return;
    const needed = mode === "doubles" ? 2 : 1;
    setOpponents((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= needed) return [...prev.slice(1), id];
      return [...prev, id];
    });
  };

  const startMatch = async () => {
    setStarting(true);
    try {
      const payload = {
        mode,
        player_character: playerChar.id,
        opponents,
        ...(mode === "doubles" ? { partner_character: partnerChar.id } : {}),
      };
      const r = await api.post("/game/match/start", payload);
      toast.success("¡Partida iniciada!");
      onStartMatch?.({
        match_id: r.data.match_id,
        mode,
        player_character: playerChar,
        partner_character: partnerChar,
        opponents: opponents.map((id) => characters.find((c) => c.id === id)).filter(Boolean),
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al iniciar partida");
    } finally { setStarting(false); }
  };

  const renderCharCard = (c, selected, onClick, disabled = false) => (
    <button
      key={c.id}
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={`relative group text-left rounded-lg overflow-hidden border-2 transition-all ${selected ? "border-[#CCFF00] scale-105 shadow-lg shadow-[#CCFF00]/30" : "border-white/10 hover:border-white/40"} ${disabled ? "opacity-30 cursor-not-allowed" : "cursor-pointer"}`}
      style={selected ? { boxShadow: `0 0 30px ${c.accent_color}40` } : {}}
      data-testid={`char-card-${c.id}`}
    >
      <div className="aspect-[3/4] bg-black relative">
        {c.portrait_url ? (
          <img src={c.portrait_url} alt={c.name} className="w-full h-full object-cover" loading="lazy" />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-800 to-black">
            <UserCircle size={64} className="text-white/20" />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent" />
        <div className="absolute top-2 left-2">
          <Badge className={`${tierColor[c.tier] || tierColor.standard} text-[9px] font-bold uppercase`}>
            {c.tier === "elite" && <><Sparkle size={10} weight="fill" className="inline mr-0.5" />Élite</>}
            {c.tier === "legendary" && <><Trophy size={10} weight="fill" className="inline mr-0.5" />Legend.</>}
            {c.tier !== "elite" && c.tier !== "legendary" && c.tier}
          </Badge>
        </div>
        {c.is_bot && (
          <div className="absolute top-2 right-2">
            <Badge className="bg-purple-500/30 text-purple-200 border-purple-500/40 text-[9px] font-bold">BOT</Badge>
          </div>
        )}
        <div className="absolute bottom-0 left-0 right-0 p-2.5">
          <div className="font-['Barlow_Condensed'] font-black uppercase tracking-tight text-white leading-tight text-sm" style={{ color: selected ? c.accent_color : "#fff" }}>
            {c.name}
          </div>
          <div className="text-[10px] text-gray-300 truncate">{c.description}</div>
        </div>
      </div>
      {/* Stats bar */}
      <div className="bg-black/80 p-2 grid grid-cols-4 gap-1 text-center">
        {["power", "speed", "control", "stamina"].map((s) => (
          <div key={s}>
            <div className="text-[8px] tracking-widest uppercase text-gray-500">{s[0]}</div>
            <div className="text-[10px] font-bold" style={{ color: c.accent_color }}>{c.stats?.[s] || 0}</div>
          </div>
        ))}
      </div>
    </button>
  );

  const emptySlot = (label, onClick) => (
    <button
      type="button"
      onClick={onClick}
      className="aspect-[3/4] rounded-lg border-2 border-dashed border-white/20 hover:border-[#CCFF00]/60 hover:bg-[#CCFF00]/5 transition-colors flex flex-col items-center justify-center text-gray-500 hover:text-[#CCFF00]"
      data-testid="char-slot-empty"
    >
      <Plus size={32} />
      <div className="text-[10px] tracking-widest uppercase mt-2">{label}</div>
      <div className="text-[8px] tracking-wider uppercase mt-1 text-gray-600">Próximamente</div>
    </button>
  );

  // ─── Render steps ───

  if (step === "mode") {
    return (
      <div className="space-y-4" data-testid="lobby-mode-step">
        <div className="text-[10px] tracking-[0.3em] uppercase text-gray-500 mb-2">Paso 1 · Modo de Juego</div>
        <div className="grid sm:grid-cols-2 gap-4">
          {["singles", "doubles"].map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => setMode(m)}
              className={`p-6 rounded-lg border-2 transition-all text-left ${mode === m ? "border-[#CCFF00] bg-[#CCFF00]/5" : "border-white/10 hover:border-white/30"}`}
              data-testid={`lobby-mode-${m}`}
            >
              <div className="text-3xl mb-2">{m === "singles" ? "👤" : "👥"}</div>
              <div className="font-['Barlow_Condensed'] text-2xl font-bold uppercase tracking-wide text-white">
                {m === "singles" ? "Singles" : "Dobles"}
              </div>
              <div className="text-xs text-gray-400 mt-1">
                {m === "singles" ? "1 vs 1" : "2 vs 2"}
              </div>
            </button>
          ))}
        </div>
        <div className="flex justify-end mt-6">
          <Button onClick={next} className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase px-8" data-testid="lobby-next-btn">
            Continuar →
          </Button>
        </div>
      </div>
    );
  }

  const stepTitle = {
    player: "Paso 2 · Tu Personaje",
    partner: "Paso 3 · Tu Compañero",
    opponents: mode === "doubles" ? "Paso 4 · Oponentes (elige 2)" : "Paso 3 · Oponente",
    ready: "¡Listo para Jugar!",
  };

  const selectedFor = step === "player" ? playerChar?.id : step === "partner" ? partnerChar?.id : null;

  return (
    <div className="space-y-4" data-testid={`lobby-${step}-step`}>
      <div className="text-[10px] tracking-[0.3em] uppercase text-gray-500">{stepTitle[step]}</div>

      {step !== "ready" && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {characters.map((c) => {
            const isPlayer = playerChar?.id === c.id;
            const isPartner = partnerChar?.id === c.id;
            const isOpponent = opponents.includes(c.id);
            let selected = false;
            let disabled = false;
            if (step === "player") selected = isPlayer;
            else if (step === "partner") { selected = isPartner; disabled = isPlayer; }
            else if (step === "opponents") { selected = isOpponent; disabled = isPlayer || isPartner; }
            return renderCharCard(c, selected, () => {
              if (step === "player") setPlayerChar(c);
              else if (step === "partner") setPartnerChar(c);
              else if (step === "opponents") toggleOpponent(c.id);
            }, disabled);
          })}
          {/* Empty slot for future custom avatars */}
          {emptySlot("Avatar Personalizado")}
        </div>
      )}

      {step === "ready" && (
        <div className="bg-[#141414] border border-[#CCFF00]/30 rounded-lg p-6">
          <div className="text-center mb-6">
            <div className="font-['Barlow_Condensed'] text-3xl font-black uppercase text-[#CCFF00] mb-2">{mode === "singles" ? "1 vs 1" : "2 vs 2"}</div>
            <div className="text-xs text-gray-400">Configuración final</div>
          </div>
          <div className="grid sm:grid-cols-2 gap-6">
            <div>
              <div className="text-[10px] tracking-widest uppercase text-blue-400 font-bold mb-2">TU EQUIPO</div>
              <div className="space-y-2">
                <div className="bg-blue-500/10 border border-blue-500/30 rounded p-2 text-sm text-white">👤 {playerChar?.name}</div>
                {mode === "doubles" && partnerChar && (
                  <div className="bg-blue-500/10 border border-blue-500/30 rounded p-2 text-sm text-white">👥 {partnerChar.name}</div>
                )}
              </div>
            </div>
            <div>
              <div className="text-[10px] tracking-widest uppercase text-red-400 font-bold mb-2">OPONENTES</div>
              <div className="space-y-2">
                {opponents.map((id) => {
                  const c = characters.find((x) => x.id === id);
                  return c ? <div key={id} className="bg-red-500/10 border border-red-500/30 rounded p-2 text-sm text-white">🤖 {c.name}</div> : null;
                })}
              </div>
            </div>
          </div>
          {dydCost > 0 && (
            <div className="mt-4 text-center text-xs text-amber-300">⚠ Se descontarán {dydCost} DYD al iniciar</div>
          )}
        </div>
      )}

      <div className="flex justify-between mt-6 gap-2">
        <Button variant="outline" onClick={back} className="border-white/10" data-testid="lobby-back-btn">
          ← Atrás
        </Button>
        {step !== "ready" ? (
          <Button onClick={next} disabled={(step === "player" && !playerChar) || (step === "partner" && !partnerChar)} className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase px-8" data-testid="lobby-next-btn">
            Continuar →
          </Button>
        ) : (
          <Button onClick={startMatch} disabled={starting} className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase px-8 text-base" data-testid="lobby-start-btn">
            {starting ? "Iniciando..." : "🎮 JUGAR"}
          </Button>
        )}
      </div>
    </div>
  );
}
